import { Controller } from "@hotwired/stimulus"
import MultiSelect from "../coreui/multi-select"
import { isMultiSelectSyncing, selectEmptyOption, syncMultiSelect } from "../shared/multi_select"

// CoreUI wraps the select, so Stimulus must stay on a stable parent element.
export default class extends Controller {
  static targets = ["select"]
  static values = { requiredMessage: String }

  connect() {
    this.mount()
  }

  disconnect() {
    this.unmount()
  }

  mount() {
    if (this.multiSelect) return

    for (const option of this.selectTarget.options) {
      if (option.dataset.resetSelected === undefined) continue

      const selected = option.selected
      option.defaultSelected = option.dataset.resetSelected === "true"
      option.selected = selected
      delete option.dataset.resetSelected
    }
    this.abortController = new AbortController()
    const options = { signal: this.abortController.signal }
    this.selectTarget.addEventListener("coreui:multi-select-refresh", () => this.refresh(), options)
    this.selectTarget.classList.toggle("disabled", this.selectTarget.disabled)
    this.multiSelect = MultiSelect.getOrCreateInstance(this.selectTarget, {
      multiple: this.selectTarget.multiple,
      required: this.selectTarget.required,
      disabled: this.selectTarget.disabled,
      id: `search-${this.selectTarget.id}`,
      requiredMessage: this.requiredMessageValue,
      optionsGroupsTemplate: (group) => {
        const label = document.createElement("span")
        label.className = "fw-semibold"
        label.textContent = group.label
        return label
      },
      // CoreUI searches option text; retain code searches without changing the labels.
      optionsTemplate: (option) => {
        const label = document.createElement("span")
        label.textContent = option.text
        const code = document.createElement("span")
        code.className = "visually-hidden"
        code.textContent = ` ${option.value}`
        label.append(code)
        return label.outerHTML
      }
    })

    this.selectTarget.addEventListener("change", () => this.nativeChanged(), options)
    this.selectTarget.form?.addEventListener("reset", () => this.formReset(), options)

    this.configureSearchInput()
    this.multiSelect.restoreState(this.cachedState || {})
    this.validate()
  }

  configureSearchInput() {
    const search = this.selectTarget.parentElement?.querySelector(".form-multi-select-search")
    if (search) {
      search.removeAttribute("name")
      const options = { signal: this.abortController.signal }
      let composing = false
      search.addEventListener("compositionstart", () => {
        composing = true
        this.selectTarget.dispatchEvent(new CustomEvent("coreui:multi-select-composition-start"))
      }, options)
      search.addEventListener("compositionend", () => {
        composing = false
        this.multiSelect.filter(search.value)
      }, options)
      // Pagila handles input events and navigation on its ancestors.
      // Leave IME candidate keys to the browser without cancelling their defaults.
      const guardComposition = (event) => {
        if (composing || event.isComposing || event.keyCode === 229) {
          event.stopImmediatePropagation()
        }
      }
      for (const type of ["input", "keydown", "keyup"]) {
        search.addEventListener(type, guardComposition, { ...options, capture: true })
      }
      search.addEventListener("keydown", (event) => {
        if (event.key === "Enter") event.preventDefault()
      }, options)
    }
  }

  unmount() {
    this.abortController?.abort()
    this.cachedState = { order: this.multiSelect?.selectionOrder }
    this.multiSelect?.dispose()
    this.multiSelect = null
  }

  prepareForCache() {
    if (!this.multiSelect) return

    // Preserve edited selections across Turbo's DOM clone without changing the
    // original defaults used by a form reset.
    for (const option of this.selectTarget.options) {
      option.dataset.resetSelected = String(option.defaultSelected)
      const selected = option.selected
      option.defaultSelected = selected
      option.selected = selected
    }
    this.unmount()
  }

  refresh() {
    this.unmount()
    this.mount()
  }

  nativeChanged() {
    if (!this.selectTarget.multiple && this.selectTarget.selectedIndex === -1) {
      selectEmptyOption(this.selectTarget)
    }
    this.validate()
    if (this.multiSelect?.dispatchingChange || isMultiSelectSyncing(this.selectTarget)) return

    syncMultiSelect(this.selectTarget)
  }

  formReset() {
    requestAnimationFrame(() => {
      if (!this.multiSelect) return

      syncMultiSelect(this.selectTarget)
      this.validate()
    })
  }

  validate() {
    const hasValue = Array.from(this.selectTarget.selectedOptions).some((option) => option.value !== "")
    const message = this.selectTarget.required && !hasValue
      ? this.requiredMessageValue
      : ""
    this.selectTarget.setCustomValidity(message)
    this.multiSelect?.search?.setCustomValidity(message)
  }
}
